<template>
  <figure class="max-table-figure" style="display:grid;gap:var(--max-gap-sm)">
    <figcaption v-if="node.title" class="max-panel__title">{{ node.title }}</figcaption>
    <div class="max-table-wrap" tabindex="0" :aria-label="node.title || undefined">
      <table class="max-table">
        <caption v-if="node.caption" class="max-table__caption">{{ node.caption }}</caption>
        <thead>
          <tr>
            <th
              v-for="col in node.columns"
              :key="col.key"
              scope="col"
              :data-align="align(col)"
              :style="widthStyle(col)"
            >{{ col.label }}</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(row, ri) in visibleRows" :key="ri" :data-intent="rowIntent(node.rules, row) ?? undefined">
            <td
              v-for="col in node.columns"
              :key="col.key"
              :data-align="align(col)"
              :data-intent="cellIntent(node.rules, row, col.key) ?? undefined"
            >{{ cell(row, col) }}</td>
          </tr>
        </tbody>
      </table>
      <div v-if="hidden > 0 || expanded" class="max-table__more">
        <button type="button" class="max-chart__toggle" @click="expanded = !expanded">
          {{ expanded ? ctx.labels.showFewerRows : ctx.labels.showAllRows }}
        </button>
        <span v-if="!expanded"> · {{ fillLabel(ctx.labels.rowsHidden, { count: hidden }) }}</span>
      </div>
    </div>
  </figure>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import { formatValue } from '../core/format.ts'
import { cellIntent, rowIntent } from '../core/rules.ts'
import { fillLabel } from '../core/labels.ts'
import type { TableColumn } from '../core/document.ts'
import { useMaxContext } from '../runtime/context.ts'

const props = defineProps<{ node: any }>()
const ctx = useMaxContext()
const expanded = ref(false)

const limit = computed(() => props.node.maxRows ?? 25)
const visibleRows = computed(() => expanded.value ? props.node.rows : props.node.rows.slice(0, limit.value))
const hidden = computed(() => Math.max(0, props.node.rows.length - limit.value))

const NUMERIC = ['number', 'integer', 'decimal', 'currency', 'percent', 'duration']
const align = (col: TableColumn) => col.align ?? (col.format && NUMERIC.includes(col.format) ? 'end' : 'start')
const widthStyle = (col: TableColumn) => col.width === 'narrow' ? { width: '1%' } : col.width === 'wide' ? { width: '40%' } : undefined
const cell = (row: Record<string, unknown>, col: TableColumn) => {
  const value = row[col.key]
  if (typeof value === 'boolean') return value ? '✓' : '–'
  return formatValue(value, { format: col.format, currency: col.currency, locale: ctx.locale, labels: ctx.labels.catalog })
}
</script>
